import React from 'react';
import { useBrand } from '../../../app/config/brand';
import type { CaseStats } from '@lds-efsp/shared-contracts';

interface CaseStatsStripProps {
  stats: CaseStats | null;
  isLoading: boolean;
}

interface StatCardProps {
  label: string;
  value: number;
  color: string;
  bgColor: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, color, bgColor }) => (
  <div
    className="flex flex-col items-center justify-center rounded-lg px-4 py-3 min-w-0"
    style={{ backgroundColor: bgColor }}
    data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <span className="text-2xl font-semibold" style={{ color }}>{value}</span>
    <span className="text-xs font-medium mt-0.5 text-center whitespace-nowrap" style={{ color }}>{label}</span>
  </div>
);

export const CaseStatsStrip: React.FC<CaseStatsStripProps> = ({ stats, isLoading }) => {
  const brand = useBrand();

  if (isLoading || !stats) {
    return (
      <div className="grid grid-cols-5 gap-3 mb-6" data-testid="stats-loading">
        {['total', 'active', 'attention', 'closed', 'counties'].map((key) => (
          <div key={key} className="h-16 rounded-lg animate-pulse" style={{ backgroundColor: brand.colors.bgLight }} />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-6" data-testid="case-stats-strip">
      <StatCard
        label="Total Cases"
        value={stats.total}
        color={brand.colors.secondary}
        bgColor={brand.colors.bgLight}
      />
      <StatCard
        label="Active"
        value={stats.active}
        color={brand.colors.link}
        bgColor={brand.colors.primaryLight}
      />
      <StatCard
        label="Needs Attention"
        value={stats.needsAttention}
        color={brand.colors.warning}
        bgColor="#FEF3C7"
      />
      <StatCard
        label="Closed This Year"
        value={stats.closedThisYear}
        color={brand.colors.textLight}
        bgColor={brand.colors.bgLight}
      />
      <StatCard
        label="Counties"
        value={stats.counties}
        color={brand.colors.secondary}
        bgColor={brand.colors.bgLight}
      />
    </div>
  );
};
